import React, { ReactNode } from "react";
import { Toaster } from "react-hot-toast";
import { Link } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import theme from "../utils/themeColors";

interface PublicLayoutProps {
  children: ReactNode;
  className?: string;
  title?: string;
  subtitle?: string;
}

/**
 * Public layout component for non-authenticated pages
 * Used for login, register, and other public pages
 */
const PublicLayout: React.FC<PublicLayoutProps> = ({
  children,
  className = "",
  title,
  subtitle,
}) => {
  const { mode, toggleMode } = useTheme();

  return (
    <div className="flex flex-col min-h-[100dvh] bg-gray-50 dark:bg-gray-900">
      {/* Header */}
      <header className="py-4 px-6 bg-white dark:bg-gray-800 shadow-sm">
        <div className="container mx-auto flex justify-between items-center">
          <Link
            to="/"
            className="text-xl font-bold text-blue-600 dark:text-blue-400"
          >
            GCCA
          </Link>

          <button
            className={`w-8 h-8 flex items-center justify-center bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 rounded-full ${theme.text.primary}`}
            onClick={toggleMode}
          >
            {mode === "light" ? (
              <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                <path d="M6 .278a.768.768 0 0 1 .08.858 7.208 7.208 0 0 0-.878 3.46c0 4.021 3.278 7.277 7.318 7.277.527 0 1.04-.055 1.533-.16a.787.787 0 0 1 .81.316.733.733 0 0 1-.031.893A8.349 8.349 0 0 1 8.344 16C3.734 16 0 12.286 0 7.71 0 4.266 2.114 1.312 5.124.06A.752.752 0 0 1 6 .278z" />
              </svg>
            ) : (
              <svg className="w-4 h-4 fill-current" viewBox="0 0 16 16">
                <path d="M8 12a4 4 0 1 0 0-8 4 4 0 0 0 0 8zM8 0a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 0zm0 13a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-1 0v-2A.5.5 0 0 1 8 13zm8-5a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2a.5.5 0 0 1 .5.5zM3 8a.5.5 0 0 1-.5.5h-2a.5.5 0 0 1 0-1h2A.5.5 0 0 1 3 8zm10.657-5.657a.5.5 0 0 1 0 .707l-1.414 1.415a.5.5 0 1 1-.707-.708l1.414-1.414a.5.5 0 0 1 .707 0zm-9.193 9.193a.5.5 0 0 1 0 .707L3.05 13.657a.5.5 0 0 1-.707-.707l1.414-1.414a.5.5 0 0 1 .707 0zm9.193 2.121a.5.5 0 0 1-.707 0l-1.414-1.414a.5.5 0 0 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .707zM4.464 4.465a.5.5 0 0 1-.707 0L2.343 3.05a.5.5 0 1 1 .707-.707l1.414 1.414a.5.5 0 0 1 0 .708z" />
              </svg>
            )}
          </button>
        </div>
      </header>

      {/* Main content */}
      <main className="flex-grow flex items-center justify-center p-4">
        <div className="w-full max-w-md">
          <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-6 mb-6">
            {/* Title and subtitle */}
            {(title || subtitle) && (
              <div className="text-center mb-6">
                {title && (
                  <h1 className={`text-2xl font-bold ${theme.text.primary}`}>
                    {title}
                  </h1>
                )}
                {subtitle && (
                  <p className={`mt-2 ${theme.text.secondary}`}>{subtitle}</p>
                )}
              </div>
            )}

            {/* Content */}
            <div className={className}>{children}</div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-4 px-6 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700">
        <div className="container mx-auto text-center">
          <p className={`text-sm ${theme.text.secondary}`}>
            &copy; {new Date().getFullYear()} GCCA Membership. All rights
            reserved.
          </p>
        </div>
      </footer>

      {/* Toast notifications */}
      <Toaster position="top-right" />
    </div>
  );
};

export default PublicLayout;
